import * as React from 'react';
import {
    AgendaViewEventProps,
    AgendaViewEventStyleProps,
    AgendaViewEventStyles,
} from './AgendaViewEvent.types';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import CalendarEventBody from 'owa-calendar-forms-common/lib/components/CalendarEventBody';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import { formatAttendeeArray } from '../../utils/formatAttendeeArray';
import { ShowAsBar, ShowAsType } from 'owa-calendar-surface';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { renderIconWrapper } from 'owa-calendar-surface-dategrid/lib/surfaceItems/CalendarSurfaceItemComponentRenderer';
import {
    eventLocationLabel,
    eventOrganizerLabel,
    eventRequiredAttendeesLabel,
    eventOptionalAttendeesLabel,
} from './AgendaViewEvent.base.locstring.json';
import loc from 'owa-localize';
import {
    getCalendarEventItemSubjectText,
    getCalendarEventItemLocationText,
} from 'owa-calendar-event-attributes';

const getClassNames = classNamesFunction<AgendaViewEventStyleProps, AgendaViewEventStyles>();

/** The base non-styled version of AgendaViewEvent. */
export function AgendaViewEventBase(props: AgendaViewEventProps) {
    const { styles, className, preview } = props;
    const classNames = getClassNames(styles, { className: className, preview: preview! });

    const { event } = props;

    if (!event) {
        return null;
    }

    const printDetailedEvent = event['printDetailedEvent'];
    if (printDetailedEvent) {
        return printDetailedEvent();
    }

    const subject = getCalendarEventItemSubjectText(event);
    const location = getCalendarEventItemLocationText(event);
    const organizer = event.Organizer && event.Organizer.Mailbox.Name;
    const requiredAttendees = formatAttendeeArray(event.RequiredAttendees);
    const optionalAttendees = formatAttendeeArray(event.OptionalAttendees);
    const colorScheme = getCalendarEventColorScheme(event);

    return (
        <div className={classNames.root}>
            <div>
                <ShowAsBar
                    className={classNames.showAsBar}
                    showAs={ShowAsType[event.FreeBusyType]}
                    colorScheme={colorScheme}
                />
            </div>
            <div className={classNames.contentWrapper}>
                <div className={classNames.subjectWrapper}>
                    <span className={classNames.subject}>{subject}</span>
                </div>
                <div className={classNames.timeWrapper}>
                    <span>{formatMeetingTimeSpan(event.Start, event.End)}</span>
                    <div className={classNames.iconWrapper}>{renderIconWrapper(event, false)}</div>
                </div>
                {location && (
                    <div>
                        <span className={classNames.fieldLabel}>{loc(eventLocationLabel)}</span>
                        <span className={classNames.fieldData}>{location}</span>
                    </div>
                )}
                {organizer && event.IsMeeting && (
                    <div>
                        <span className={classNames.fieldLabel}>{loc(eventOrganizerLabel)}</span>
                        <span className={classNames.fieldData}>{organizer}</span>
                    </div>
                )}
                {requiredAttendees && event.IsMeeting && (
                    <div>
                        <span className={classNames.fieldLabel}>
                            {loc(eventRequiredAttendeesLabel)}
                        </span>
                        <span className={classNames.fieldData}>{requiredAttendees}</span>
                    </div>
                )}
                {optionalAttendees && event.IsMeeting && (
                    <div>
                        <span className={classNames.fieldLabel}>
                            {loc(eventOptionalAttendeesLabel)}
                        </span>
                        <span className={classNames.fieldData}>{optionalAttendees}</span>
                    </div>
                )}
                {event?.Body?.Value && (
                    <div className={classNames.bodyWrapper}>
                        <CalendarEventBody calendarEvent={event} viewType={'AgendaView'} />
                    </div>
                )}
            </div>
        </div>
    );
}
